// Short synthesized chime for phone dial-in callers joining or leaving a
// session. It never throws: a missing or blocked AudioContext (unsupported
// browser, autoplay policy) must not affect the video session.

// Chimes of the same kind within this window are played once, so several
// callers that are already connected when we join do not stack up.
export const PHONE_CALL_CHIME_COALESCE_MS = 1000;

const TONE_SECONDS = 0.12;
const TONE_GAP_SECONDS = 0.02;
const PEAK_GAIN = 0.15;

// Rising pair for join, falling pair for leave.
const TONES = {
  join: [660, 880],
  leave: [880, 660],
};

let audioContext = null;
const lastPlayedAt = {};

// Output device for the chime: '' is the system default. The session audio is
// routed with OT.setAudioOutputDevice, so the chime has to follow the speaker
// the user picked or it plays from another device (and can leak into the room).
let desiredSinkId = '';
let appliedSinkId = '';
const MAX_SINK_ATTEMPTS = 3;

export function setPhoneCallChimeOutputDevice(deviceId) {
  desiredSinkId = !deviceId || deviceId === 'default' ? '' : deviceId;
}

const getAudioContext = () => {
  if (audioContext) return audioContext;
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  audioContext = new AudioContextClass();
  return audioContext;
};

// Returns false when the chime must not play: the selected device could not be
// applied, and playing on the default output would sound on the wrong device.
// Browsers without AudioContext.setSinkId (Safari, Firefox) keep playing on the
// default output.
const applyOutputDevice = async context => {
  if (typeof context.setSinkId !== 'function') return true;
  // The selection can change while setSinkId is pending, so re-check.
  for (let attempt = 0; attempt < MAX_SINK_ATTEMPTS; attempt += 1) {
    const target = desiredSinkId;
    if (target === appliedSinkId) return true;
    try {
      // eslint-disable-next-line no-await-in-loop
      await context.setSinkId(target);
      appliedSinkId = target;
    } catch {
      return false;
    }
  }
  return desiredSinkId === appliedSinkId;
};

const playTone = (context, frequency, startTime) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const endTime = startTime + TONE_SECONDS;

  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;
  // Short attack and release so the tone does not click.
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(PEAK_GAIN, startTime + 0.01);
  gain.gain.linearRampToValueAtTime(0, endTime);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.onended = () => {
    oscillator.disconnect();
    gain.disconnect();
  };
  oscillator.start(startTime);
  oscillator.stop(endTime);
};

export async function playPhoneCallChime(kind) {
  try {
    const frequencies = TONES[kind];
    if (!frequencies) return;

    const now = Date.now();
    if (
      lastPlayedAt[kind] !== undefined &&
      now - lastPlayedAt[kind] < PHONE_CALL_CHIME_COALESCE_MS
    ) {
      return;
    }
    lastPlayedAt[kind] = now;

    const context = getAudioContext();
    if (!context) return;
    if (context.state === 'suspended') await context.resume();
    // Scheduling on a context that is not running would queue the tones until
    // it wakes up later, so only play when it is actually running.
    if (context.state !== 'running') return;

    if (!(await applyOutputDevice(context))) {
      // Nothing was played, so do not suppress the next chime of this kind.
      delete lastPlayedAt[kind];
      return;
    }

    frequencies.forEach((frequency, index) => {
      playTone(
        context,
        frequency,
        context.currentTime + index * (TONE_SECONDS + TONE_GAP_SECONDS),
      );
    });
  } catch {
    // Audio is best effort.
  }
}

export function resetPhoneCallChimeForTesting() {
  audioContext = null;
  desiredSinkId = '';
  appliedSinkId = '';
  Object.keys(lastPlayedAt).forEach(kind => {
    delete lastPlayedAt[kind];
  });
}
